import { Grid, GridProps } from '@progress/kendo-react-grid';
import { MutableRefObject, ReactNode, RefObject, useLayoutEffect, useRef } from 'react';

export const useFitColumns = (
  gridRef: MutableRefObject<Grid | undefined>,
  containerRef: RefObject<HTMLDivElement> | undefined,
  columnIds: string[],
  columnElements: ReactNode,
  data: GridProps['data'],
  width: number,
  height?: number,
): void => {
  const checkSize = useRef<() => void>();

  useLayoutEffect(() => {
    const container = containerRef?.current;
    if (!columnIds.length || !container) {
      return;
    }

    let frame: number | undefined;
    let availableWidth = container.clientWidth;
    let availableHeight = container.clientHeight;
    const fit = () => {
      frame = undefined;
      gridRef.current?.fitColumns(columnIds);
    };
    const scheduleFit = () => {
      if (frame === undefined) {
        frame = window.requestAnimationFrame(fit);
      }
    };
    const onResize = () => {
      const nextWidth = container.clientWidth;
      const nextHeight = container.clientHeight;
      if (nextWidth !== availableWidth || nextHeight !== availableHeight) {
        availableWidth = nextWidth;
        availableHeight = nextHeight;
        scheduleFit();
      }
    };

    // Text/child changes also catch callers mutating records in place. Ignore
    // the column width attributes written by Kendo's own fitting operation.
    const mutationObserver = typeof MutationObserver === 'undefined'
      ? undefined
      : new MutationObserver(scheduleFit);
    const resizeObserver = typeof ResizeObserver === 'undefined'
      ? undefined
      : new ResizeObserver(onResize);

    fit();
    checkSize.current = onResize;
    mutationObserver?.observe(container, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['src', 'value', 'title', 'alt'],
    });
    resizeObserver?.observe(container);
    window.addEventListener('resize', onResize);
    container.addEventListener('load', scheduleFit, true);

    return () => {
      checkSize.current = undefined;
      mutationObserver?.disconnect();
      resizeObserver?.disconnect();
      window.removeEventListener('resize', onResize);
      container.removeEventListener('load', scheduleFit, true);
      if (frame !== undefined) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [gridRef, containerRef, columnIds, columnElements, data, width, height]);

  useLayoutEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      checkSize.current?.();
    }
  });
};
